import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { UserProvider, useUser } from './UserContext';

// Stamps a localStorage timestamp every time the user visits ANY page that
// isn't /free-activity. The free-activity intro screen reads this stamp to
// decide whether the visitor is "warm" (came from elsewhere on the site,
// even via a new tab or refresh — localStorage is shared across tabs in
// the same browser) or "cold" (landed directly from an external ad).
// Cold landers see the intro; warm visitors skip it.
const WarmVisitTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname !== '/free-activity') {
      try { localStorage.setItem('br_warm_visit_ts', String(Date.now())); } catch {}
    }
  }, [location.pathname]);

  // Capture ?ref=CODE on first load and persist it so the signup flow can attribute the new user
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const ref = params.get('ref');
      if (ref) localStorage.setItem('rb_referral', ref.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20));
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

// Error Boundary — catches render crashes and shows a recovery screen
// instead of a black screen (especially important on mobile Safari)
class ErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(to bottom, #fffbeb, #fff7ed, #fdf2f8)', padding: 24 }}>
          <div style={{ textAlign: 'center', maxWidth: 400 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📚</div>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Something went wrong</h2>
            <p style={{ color: '#6b7280', marginBottom: 24 }}>Sorry about that! Let's get you back on track.</p>
            <button
              onClick={() => { this.setState({ hasError: false }); window.location.replace('/'); }}
              style={{ background: 'linear-gradient(to right, #8b5cf6, #ec4899)', color: 'white', border: 'none', padding: '12px 32px', borderRadius: 12, fontWeight: 700, fontSize: 16, cursor: 'pointer' }}
            >
              Back to Home
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
import HomePage from './Pages/HomePage';
import AboutPage from './Pages/AboutPage';
import DashboardPage from './Pages/DashboardPage';
import CurriculumPage from './Pages/CurriculumPage';
import LoginPage from './Pages/LoginPage';
import GetStartedPage from './Pages/GetStartedPage';
import LessonPage from './Pages/LessonPage';
import ProfilePage from './Pages/ProfilePage';
import SettingsPage from './Pages/SettingsPage';
import ResearchPage from './Pages/ResearchPage';
import ParentGuidePage from './Pages/ParentGuidePage';
import FAQPage from './Pages/FAQPage';
import GamePage from './Pages/GamePage';
import TermsPage from './Pages/TermsPage';
import PrivacyPage from './Pages/PrivacyPage';
import ContactPage from './Pages/ContactPage';
import UpgradePage from './Pages/UpgradePage';
import PaymentSuccess from './Pages/PaymentSuccess';
import PaymentCancelled from './Pages/PaymentCancelled';
import AdminDashboardPage from './Pages/AdminDashboardPage';
import AuthActionPage from './Pages/AuthActionPage';
import TryPage from './Pages/TryPage';
import AdminRoute from './components/AdminRoute';
import './App.css';

// Protected Route wrapper — prevents transient auth null states from
// kicking the user to login. On mobile, save-password prompts, tab
// backgrounding, and token refreshes can briefly null Firebase auth.
// UserContext sets loading=true during the grace period, so we just
// trust the loading flag here. No localStorage checks needed.
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useUser();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-pink-50">
        <div className="text-center">
          <div className="text-4xl mb-4">📚</div>
          <div className="text-xl text-gray-700">Loading...</div>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

// Replaces the plain <Navigate to="/" replace /> catch-all so we can capture
// which broken URL triggered the redirect. The unmatched path is passed via
// router state → HomePage's usePageView reads it as navSource → the admin
// dashboard event shows the original URL that 404'd.
const CatchAllRedirect = () => {
  const loc = useLocation();
  return <Navigate to="/" replace state={{ navSource: `404_catchall:${loc.pathname}${loc.search}` }} />;
};

function App() {
  return (
    <ErrorBoundary>
    <UserProvider>
      <Router>
        <WarmVisitTracker />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/try" element={<TryPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/get-started" element={<GetStartedPage />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/parent-guide" element={<ParentGuidePage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/auth/action" element={<AuthActionPage />} />

          {/* Free Lesson - Public Access */}
          <Route path="/lesson/1" element={<LessonPage />} />

          {/* Free Trial - Isolated instance, no sign-in */}
          <Route path="/free-activity" element={<LessonPage freeTrialMode />} />
          
          {/* Upgrade Page - Protected */}
          <Route 
            path="/upgrade" 
            element={
              <ProtectedRoute>
                <UpgradePage />
              </ProtectedRoute>
            } 
          />
          
          {/* Payment Routes - Protected */}
          <Route 
            path="/payment-success" 
            element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/payment-cancelled" 
            element={
              <ProtectedRoute>
                <PaymentCancelled />
              </ProtectedRoute>
            } 
          />
          
          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/curriculum" 
            element={
              <ProtectedRoute>
                <CurriculumPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/lesson/:lessonId" 
            element={
              <ProtectedRoute>
                <LessonPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/games" 
            element={
              <ProtectedRoute>
                <GamePage />
              </ProtectedRoute>
            } 
          />
          
          {/* Admin Route */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboardPage />
              </AdminRoute>
            }
          />

          {/* Catch-all - redirect to home. Passes the unmatched URL via
              router state so HomePage's page_view event captures which broken
              path triggered the redirect (visible in admin dashboard metadata). */}
          <Route path="*" element={<CatchAllRedirect />} />
        </Routes>
      </Router>
    </UserProvider>
    </ErrorBoundary>
  );
}

export default App;